import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Car, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, LoadingState, ErrorState, EmptyState, ConfirmDialog } from "@/components/common";
import { VehicleForm } from "@/components/pro/VehicleForm";
import { DuplicateWarning } from "@/components/pro/CustomerForm";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtNumber, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

export default function Vehicles() {
  const { readOnly } = useGarage();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const { data, error, loading, reload } = useFetch("/vehicles", { q: query });
  const customers = useFetch("/customers");
  const [dlg, setDlg] = useState(() => (new URLSearchParams(window.location.search).get("new") === "1" ? {} : null));
  const [del, setDel] = useState(null);
  const [dups, setDups] = useState(null);

  const remove = async () => {
    try {
      await api.post(`/vehicles/${del.id}/archive`);
      toast.success(t("archive.done"));
      setDel(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const noCustomers = customers.data && customers.data.length === 0;
  const addBtn = !readOnly && (
    <Button onClick={() => { setDups(null); setDlg({}); }} disabled={noCustomers} data-testid="new-vehicle-button"><Plus className="mr-2 h-4 w-4" /> {t("vehicles.add")}</Button>
  );

  return (
    <div>
      <PageHeader title={t("pro.nav.vehicles")} subtitle={noCustomers ? t("vehicles.needCustomer") : t("vehicles.subtitle")} actions={addBtn} />
      <form onSubmit={(e) => { e.preventDefault(); setQuery(q); }} className="mb-6 flex max-w-lg gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("vehicles.search")} className="pl-9" data-testid="vehicle-search-input" />
        </div>
        <Button type="submit" variant="outline" data-testid="vehicle-search-button">{t("common.search")}</Button>
      </form>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState icon={Car} title={query ? t("vehicles.noResults") : t("vehicles.empty")} text={query ? "" : t("vehicles.emptyText")} action={!query && addBtn} testId="vehicles-empty-state" />}
      {data && data.length > 0 && (
        <div className="gf-card overflow-x-auto">
          <Table data-testid="vehicles-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("vehicles.plate")}</TableHead>
                <TableHead>{t("vehicles.vehicle")}</TableHead>
                <TableHead>{t("vehicles.odometer")}</TableHead>
                <TableHead>{t("vehicles.owner")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((v) => (
                <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                  <TableCell className="whitespace-nowrap"><Link to={`/pro/vehicles/${v.id}`} className="rounded border-2 border-slate-800 px-1.5 font-mono-plate text-sm font-semibold hover:bg-yellow-100" data-testid={`vehicle-open-${v.id}`}>{v.plate}</Link> <span className="text-xs text-slate-500">{v.state}</span></TableCell>
                  <TableCell>{v.make} {v.model} {v.year ? `(${v.year})` : ""}</TableCell>
                  <TableCell className="text-sm">{v.odometer != null ? `${fmtNumber(v.odometer)} km · ${fmtYmd(v.odometer_date)}` : "—"}</TableCell>
                  <TableCell><Link to={`/pro/customers/${v.customer_id}`} className="text-sm font-medium text-teal-700 hover:underline" data-testid={`vehicle-owner-link-${v.id}`}>{v.customer_name}</Link></TableCell>
                  <TableCell className="text-right">
                    {!readOnly && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setDups(null); setDlg({ vehicle: v }); }} data-testid={`edit-vehicle-${v.id}`} aria-label={t("common.edit")}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-red-600" onClick={() => setDel(v)} data-testid={`archive-vehicle-${v.id}`} aria-label={t("archive.archive")} title={t("archive.archive")}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle>{dlg?.vehicle ? t("vehicles.edit") : t("vehicles.add")}</DialogTitle><DialogDescription>{t("vehicles.dialogHint")}</DialogDescription></DialogHeader>
          <DuplicateWarning matches={dups} />
          {dlg && <VehicleForm vehicle={dlg.vehicle} basePath="/vehicles" customers={customers.data || []} onDuplicate={setDups} onSaved={() => { setDlg(null); toast.success(t("common.saved")); reload(); }} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title={t("archive.vehicleTitle")} description={t("archive.vehicleText", { plate: del?.plate })} confirmLabel={t("archive.archive")} onConfirm={remove} testId="archive-vehicle-dialog" />
    </div>
  );
}
